import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';

import { useLocales, useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function ReceptionLayout({ children }: Props) {
  const { t } = useTranslate();
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { onChangeLang } = useTranslate();
  const { allLangs, currentLang } = useLocales();
  const languagePopover = usePopover();

  const NAV_ITEMS = [
    { label: t('New Reception'), path: '/reception/new', icon: 'solar:qr-code-bold' },
    { label: t('Receptions'), path: '/reception/list', icon: 'solar:list-bold' },
    {
      label: t('New Distribution'),
      path: '/reception/distribution/new',
      icon: 'solar:box-bold',
    },
    {
      label: t('Distributions'),
      path: '/reception/distribution/list',
      icon: 'solar:document-text-bold',
    },
  ];

  const currentNav = NAV_ITEMS.findIndex((item) => location.pathname.startsWith(item.path));

  const handleChangeLang = (newLang: string) => {
    onChangeLang(newLang);
    languagePopover.onClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top Bar */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Jidra Reception
          </Typography>

          {user && (
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                onClick={languagePopover.onOpen}
                sx={{
                  width: 40,
                  height: 40,
                  ...(languagePopover.open && {
                    bgcolor: 'action.selected',
                  }),
                }}
              >
                <Iconify icon={currentLang.icon} sx={{ borderRadius: 0.65, width: 28 }} />
              </IconButton>

              <Typography variant="body2" color="text.secondary">
                {user.name || user.email}
              </Typography>
              <Button
                size="small"
                color="inherit"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Logout
              </Button>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <CustomPopover
        open={languagePopover.open}
        onClose={languagePopover.onClose}
        sx={{ width: 160 }}
      >
        {allLangs.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === currentLang.value}
            onClick={() => handleChangeLang(option.value)}
          >
            <Iconify icon={option.icon} sx={{ borderRadius: 0.65, width: 28 }} />
            {option.label}
          </MenuItem>
        ))}
      </CustomPopover>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>{children}</Box>

      {/* Bottom Navigation */}
      <BottomNavigation
        showLabels
        value={currentNav >= 0 ? currentNav : 0}
        onChange={(_, newValue) => {
          navigate(NAV_ITEMS[newValue].path);
        }}
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          '& .MuiBottomNavigationAction-root': { minWidth: 0 },
        }}
      >
        {NAV_ITEMS.map((item) => (
          <BottomNavigationAction
            key={item.path}
            label={item.label}
            icon={<Iconify icon={item.icon} width={24} />}
          />
        ))}
      </BottomNavigation>
    </Box>
  );
}
